import React from 'react';
import styles from './BroadcastJobProgress.module.css';
import './shared/campaignConsole.css';

// Shared aggregate-progress view for a broadcast job — rendered from
// CampaignLaunch.js for both an immediate send and a scheduled campaign
// that has since fired (scheduling was folded into CampaignLaunch.js, not
// kept as a separate step). Deliberately dumb/presentational: the caller
// owns its own polling, error handling, and terminal-state side effects
// (toasts, wallet refresh) — this only ever renders whatever
// `broadcastJob` it's handed, so a campaign that just fired looks
// identical regardless of whether it got there via Launch Now or a
// schedule.
const STATUS_CHIP_CLASS = {
    completed: 'ccChipGood',
    cancelled: 'ccChipNeutral',
    failed: 'ccChipDanger',
};

const BroadcastJobProgress = ({ audienceCount, broadcastJob, pollTimedOut, idleMessage = 'Ready to launch.' }) => {
    const total = broadcastJob?.total ?? audienceCount ?? 0;
    const submitted = broadcastJob?.submitted ?? 0;
    const pct = Math.min(100, Math.round((submitted / Math.max(1, total)) * 100));
    const chipClass = STATUS_CHIP_CLASS[broadcastJob?.status] || 'ccChipAccent';

    return (
        <div className={styles.broadcastProgressWrapper}>
            {broadcastJob ? (
                <div className={styles.broadcastProgress}>
                    <div className={styles.broadcastHeroRow}>
                        <div>
                            <div className={styles.broadcastEyebrow}>Delivery progress</div>
                            <div className={styles.broadcastBigNumber}>
                                {submitted}
                                <span className={styles.broadcastOf}> / {total}</span>
                            </div>
                        </div>
                        <span className={`ccChip ${chipClass}`}>{broadcastJob.status || 'processing'}</span>
                    </div>
                    <div className={styles.broadcastBarTrack}>
                        <div className={styles.broadcastBarFill} style={{ width: `${pct}%` }} />
                    </div>
                    <div className={styles.broadcastChipRow}>
                        <span className="ccChip ccChipGood">{broadcastJob.delivered ?? 0} delivered</span>
                        <span className="ccChip ccChipAccent">{broadcastJob.read ?? 0} read</span>
                        {(broadcastJob.failed ?? 0) > 0 && (
                            <span className="ccChip ccChipDanger">{broadcastJob.failed} failed</span>
                        )}
                        {broadcastJob.skipped != null && (
                            <span className="ccChip ccChipNeutral">{broadcastJob.skipped} skipped</span>
                        )}
                    </div>
                </div>
            ) : (
                <div className={styles.broadcastIdle}>
                    <div className={styles.broadcastAudienceLine}>
                        {audienceCount ?? 0} recipients in this campaign's audience.
                    </div>
                    {idleMessage}
                </div>
            )}
            {pollTimedOut && (
                <div className={styles.broadcastTimedOut}>
                    This is taking longer than expected. The campaign is still processing in the background — check back on this page later for its final status.
                </div>
            )}
        </div>
    );
};

export default BroadcastJobProgress;
